import { Button, Input, LoadingIndicator } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React, { ChangeEvent, useState } from "react";
import { FormattedMessage } from "react-intl";
import useIntlDate from "~/lib/hooks/useIntlDate";
import { IStripePlanFieldsFragment } from "~/lib/queries/fragments/__generated__/StripePlanFields.generated";
import Strongify from "~/lib/utils/strongify";
import mediaQueries from "~/components/mediaQueries";

interface IConfirmDowngradeProps {
  loading: boolean;
  currentPlan: IStripePlanFieldsFragment;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDowngrade({ loading, currentPlan, onConfirm, onCancel }: IConfirmDowngradeProps) {
  const [canConfirm, setCanConfirm] = useState(false);
  const intlDate = useIntlDate();

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 20,
        padding: 20,
        backgroundColor: palette.white,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 4,
      }}
    >
      <div
        css={{
          display: "grid",
          gridRowGap: 20,
        }}
      >
        <h2
          css={{
            margin: 0,
          }}
        >
          <FormattedMessage defaultMessage="Confirm Downgrade" id="PHQLEr" />
        </h2>

        <p
          css={{
            margin: 0,
            color: palette.mediumGray,
            fontSize: 14,
            lineHeight: 1.33,
          }}
        >
          <FormattedMessage
            defaultMessage="Change will be effective at the end of your billing period on <strong>{date}</strong>." id="H1szLa"
            values={{
              date: intlDate.format(currentPlan.changeDetails.proratePaymentDueDate),
              strong: (chunk: any) => Strongify(chunk),
            }}
          />
        </p>

        <p
          css={{
            margin: 0,
            color: palette.mediumGray,
            fontSize: 14,
            lineHeight: 1.33,
          }}
        >
          <FormattedMessage
            defaultMessage="Any newly added content or users that exceed your new plan’s allowances will be erased immediately prior to
          the above date." id="1tP09i"
          />
        </p>

        <p
          css={{
            margin: 0,
            color: palette.mediumGray,
            fontSize: 14,
            lineHeight: 1.33,
          }}
        >
          <FormattedMessage defaultMessage="Type DOWNGRADE (in ALL CAPS) to proceed with your account change." id="pCp0yB" />
        </p>

        <Input
          placeholder="DOWNGRADE"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setCanConfirm((event.target.value || "").trim() === "DOWNGRADE");
          }}
        />
      </div>

      <div
        css={mediaQueries({
          justifyContent: "center",
          display: "grid",
          gridTemplateColumns: ["1fr", "1fr", "1fr", "1fr 1fr"],
          gridColumnGap: 20,
          gridRowGap: 20,
        })}
      >
        <Button
          color={canConfirm ? palette.innovatorBlue : palette.darkGray}
          theme={canConfirm ? "primary" : "secondary"}
          onClick={onConfirm}
          disabled={!canConfirm}
        >
          {loading ? <LoadingIndicator /> : <FormattedMessage defaultMessage="Confirm" id="N2IrpM" />}
        </Button>

        <Button color={palette.darkGray} theme="primary" disabled={loading} onClick={onCancel}>
          <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
        </Button>
      </div>
    </div>
  );
}
